import { createFileRoute, Link } from "@tanstack/react-router";
import { Crosshair, ShieldCheck, FileCheck, Building2, Scale, Ban } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";

const TITLE = "About the Desk | XAU Pro Academy";
const DESC =
  "XAU Pro Academy is an educational and quantitative research desk for XAUUSD market structure, operated by Orion AI Technologies. Learn about our methodology, operating principles, and what we do not do.";

const principles = [
  {
    icon: Crosshair,
    title: "Capital preservation over trade frequency",
    desc: "Missing a trade is acceptable. Low-quality setups are rejected. Neutral bias is a valid output, and no bias is ever forced to maintain activity.",
  },
  {
    icon: Scale,
    title: "Precision over prediction",
    desc: "The desk does not forecast. It qualifies conditions: market structure, liquidity, displacement, and session context are scored before any setup is published.",
  },
  {
    icon: FileCheck,
    title: "Confirmation over anticipation",
    desc: "Every published decision is logged with its reasoning, grade, and concerns. Outcomes, including losses, are archived and reviewable in the Case Studies record.",
  },
  {
    icon: ShieldCheck,
    title: "Institutional execution over retail patterns",
    desc: "The methodology is built on Smart Money Concepts: structure, liquidity, displacement, and POI qualification. Retail indicator logic is deprioritized by design.",
  },
];

const notList = [
  "We are not a broker, investment adviser, or licensed financial institution.",
  "We do not manage funds, hold client capital, or execute trades on behalf of users.",
  "We do not provide personalized investment recommendations or financial advice.",
  "We do not guarantee profits, returns, or performance outcomes of any kind.",
  "We do not receive compensation from brokers, prop firms, or third parties referenced on this platform.",
];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://xauproacademy.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
            { "@type": "ListItem", position: 2, name: "About", item: "https://xauproacademy.com/about" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: TITLE,
          description: DESC,
          url: "https://xauproacademy.com/about",
          mainEntity: {
            "@type": "Organization",
            name: "XAU Pro Academy",
            legalName: "Orion AI Technologies",
            url: "https://xauproacademy.com/",
            email: "support@xauproacademy.com",
            identifier: { "@type": "PropertyValue", name: "License No.", value: "58070" },
            sameAs: ["https://x.com/XAUProAcademy", "https://www.youtube.com/@XAUProAcademy"],
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <Layout>
      <article className="container mx-auto max-w-4xl px-4 py-16 lg:py-20">
        <header className="max-w-2xl">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            About the Desk
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
            An educational research desk for XAUUSD market structure.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            XAU Pro Academy is an educational and quantitative research platform
            focused on gold market structure analysis. The platform is operated by
            Orion AI Technologies (License No. 58070) and publishes market analysis,
            an audited internal trade log, and institutional-grade educational
            material for training purposes only.
          </p>
        </header>

        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">What the desk does</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The desk applies a single, fixed Smart Money Concepts rule set to the
            XAUUSD market. Every setup is scored by the Orion decision engine across
            structure, liquidity, displacement, POI quality, session context, and
            risk-to-reward. Setups that do not meet the criteria are rejected, not
            softened. The output of that process is published as analysis, an
            internal trade log, and archived case studies.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            All content is produced for analytical and educational purposes. It is
            not financial advice, and it is not a solicitation to buy or sell any
            instrument.
          </p>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">Operating principles</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.title}
                className="rounded-xl border border-border/60 bg-card/60 p-5"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-sm font-semibold leading-snug">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">Transparency and record keeping</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The desk maintains a public, audited record of its internal trade log.
            Wins and losses are published with identical detail: entry, stop,
            targets, grade, and outcome. Every closed setup is archived as a case
            study with its full reasoning chain. Past performance is published for
            transparency and is not indicative of future results.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/track-record">Performance Record</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/case-studies">Case Studies Archive</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/validation">Validation Hub</Link>
            </Button>
          </div>
        </section>

        <section className="mt-14 rounded-xl border border-border/60 bg-muted/30 p-6">
          <div className="flex items-center gap-2">
            <Ban className="h-4 w-4 text-gold" />
            <h2 className="font-display text-xl font-semibold">What the desk does not do</h2>
          </div>
          <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-muted-foreground">
            {notList.map((item) => (
              <li key={item} className="flex items-start gap-2.5">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gold" />
            <h2 className="font-display text-xl font-semibold">Operator and contact</h2>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            XAU Pro Academy is operated by Orion AI Technologies, License No. 58070.
            Support coverage runs Monday to Friday, and the desk typically replies
            within 24 business hours.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="sm">
              <Link to="/contact">Contact the Desk</Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/risk-disclaimer">Read the Risk Disclaimer</Link>
            </Button>
          </div>
        </section>
      </article>
    </Layout>
  );
}
